<template>
    <article class="quote-form-container">
        <div class="quote-form">
            <div
                class="quote-form__header"
                :class="{
                    'quote-form__header--mobile-background': props.enableMobileBackground,
                }"
            >
                <WatsonAtomDynamicText
                    v-if="props.heading"
                    :text="props.heading"
                    :typography="props.typography.heading"
                    class="quote-form__heading"
                />

                <WatsonAtomDynamicText
                    v-if="props.nudgeText"
                    :text="props.nudgeText"
                    :typography="props.typography.nudgeText"
                    class="quote-form__nudge-text"
                />
            </div>
            <div class="quote-form__content">
                <ul
                    v-if="items.length"
                    class="quote-form__list"
                >
                    <li
                        v-for="(item, index) in items"
                        :key="index"
                        class="quote-form__item"
                        :class="{
                            'quote-form__item--border': props.enableAnswerBorder,
                        }"
                    >
                        <button
                            class="quote-form__item-container"
                            @click="buttonClick(item.answer)"
                        >
                            <div
                                v-if="item.image"
                                class="quote-form__item-image-wrapper"
                            >
                                <WatsonAtomImage
                                    v-if="item.image"
                                    :content="item.image"
                                    class="quote-form__item-image"
                                />
                            </div>
                            <div class="quote-form__item-button">
                                <WatsonAtomDynamicText
                                    v-if="item.text"
                                    :text="item.text"
                                    :typography="props.typography.button"
                                    class="quote-form__item-text"
                                />
                            </div>
                        </button>
                    </li>
                </ul>
                <div class="quote-form__banner">
                    <!-- eslint-disable vue/no-v-html -->
                    <div
                        class="quote-form__item-text"
                        v-html="props.bannerText"
                    ></div>
                    <!-- eslint-enable vue/no-v-html -->
                </div>
            </div>
        </div>
    </article>
</template>

<script setup>
import { storeToRefs } from 'pinia';
import { useQuoteForm } from '../../js/stores/quote-form';

import WatsonAtomDynamicText from '@atoms/dynamic-text/watson-atom-dynamic-text.vue';
import WatsonAtomImage from '@atoms/image/watson-atom-image.vue';

const props = defineProps({
    items: {
        type: Array,
        required: true,
    },
    zoeRedirect: {
        type: String,
        required: true,
    },
    heading: {
        type: String,
        required: true,
    },
    nudgeText: {
        type: String,
        required: true,
    },
    enableMobileBackground: {
        type: Boolean,
        required: false,
        default: false,
    },
    enableAnswerBorder: {
        type: Boolean,
        required: false,
        default: false,
    },
    bannerText: {
        type: String,
        required: true,
    },
    uspsTurnOff: {
        type: Boolean,
        required: false,
        default: false,
    },
    background: {
        type: String,
        required: false,
        default: null,
    },
    headerBackground: {
        type: String,
        required: false,
        default: null,
    },
    headerTextColorDesktop: {
        type: String,
        required: false,
        default: null,
    },
    headerTextColor: {
        type: String,
        required: false,
        default: null,
    },
    answerBorder: {
        type: String,
        required: false,
        default: null,
    },
    answerActiveBorder: {
        type: String,
        required: false,
        default: null,
    },
    buttonBackground: {
        type: String,
        required: false,
        default: null,
    },
    buttonHover: {
        type: String,
        required: false,
        default: null,
    },
    buttonTextColor: {
        type: String,
        required: false,
        default: null,
    },
    bannerBackground: {
        type: String,
        required: false,
        default: null,
    },
    bannerTextColor: {
        type: String,
        required: false,
        default: null,
    },
    uspsData: {
        type: Object,
        required: false,
        default: () => ({}),
    },
    typography: {
        type: Object,
        required: false,
        default: () => ({}),
    },
});

const quoteForm = useQuoteForm();
const { aaci, aacri } = storeToRefs(quoteForm);

const buttonClick = answer => {
    const url = new URL(props.zoeRedirect);
    url.searchParams.append('first_question', answer);

    if (aaci.value) {
        url.searchParams.append('aaci', aaci.value);
    }

    if (aacri.value) {
        url.searchParams.append('aacri', aacri.value);
    }

    const originUrl = new URL(window.location.href);
    const gclid = originUrl.searchParams.get('gclid');
    originUrl.searchParams.delete('gclid');
    url.searchParams.append('origin', originUrl);
    url.searchParams.append('gclid', gclid);

    window.location.href = url.href;
};
</script>

<style lang="scss" scoped>
@use 'watson-theme-quote-form';

@layer theme {
    .quote-form-container {
        --background: v-bind('background');
        --header-background: v-bind('headerBackground');
        --header-text-color-desktop: v-bind('headerTextColorDesktop');
        --header-text-color: v-bind('headerTextColor');
        --button-background: v-bind('buttonBackground');
        --button-hover: v-bind('buttonHover');
        --answer-border: v-bind('answerBorder');
        --answer-active-border: v-bind('answerActiveBorder');
        --button-text-color: v-bind('buttonTextColor');
        --banner-background: v-bind('bannerBackground');
        --banner-text-color: v-bind('bannerTextColor');
    }
}
</style>
